import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/common/Layout';
import StatusSection from '../components/projects/StatusSection';
import ChatBox from '../components/chat/ChatBox';
import History from '../components/chat/History';
import { projectAPI } from '../services/api';
import { ArrowLeft, Loader2 } from 'lucide-react';

export default function Project() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('chat'); // 'chat' or 'history'

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const res = await projectAPI.get(id);
        setProject(res.data.project || res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [id]);

  if (loading) {
    return (
      <Layout title="Project">
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 size={48} className="animate-spin text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 font-medium">Loading project...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!project) {
    return (
      <Layout title="Project">
        <div className="text-center py-20">
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Project not found</h3>
          <button
            onClick={() => navigate('/')}
            className="mt-4 px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-all"
          >
            Back to Dashboard
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={project.name}>
      <div className="space-y-6">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="font-medium">Back to Projects</span>
        </button>

        {/* Project Header */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{project.name}</h1>
          <p className="text-gray-600">
            {project.repo_owner}/{project.repo_name}
          </p>
        </div>

        {/* Status Section */}
        <StatusSection projectId={id} />

        {/* Tabs */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="border-b border-gray-200 bg-gray-50 px-6">
            <div className="flex gap-6">
              <button
                onClick={() => setActiveTab('chat')}
                className={`py-4 px-2 font-medium transition-all relative ${
                  activeTab === 'chat'
                    ? 'text-black'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Chat
                {activeTab === 'chat' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black" />
                )}
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`py-4 px-2 font-medium transition-all relative ${
                  activeTab === 'history'
                    ? 'text-black'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                History
                {activeTab === 'history' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black" />
                )}
              </button>
            </div>
          </div>

          <div className="p-6">
            {activeTab === 'chat' ? (
              <ChatBox projectId={id} />
            ) : (
              <History projectId={id} />
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}